/**
 * socialPresets.ts — canonical, platform-aware recording recipes.
 *
 * Both the mobile Reel UI and the desktop Reel Studio import from here so
 * "Quick Start" buttons stay aligned with each platform's actual specs and
 * we get a single place to update when a platform changes its rules.
 *
 * Each preset captures three things:
 *
 *   1. **Aspect** — used by the existing `data-aspect` framing on
 *      `viz/index.html` + `viz/mobile.html` so the live canvas already
 *      shows the user exactly what will be recorded.
 *   2. **Duration recipe** — `lengthSec` is our default-tuned sweet spot
 *      (NOT each platform's hard maximum). We keep clips short on purpose
 *      because shorter, denser content performs better as discovery feed
 *      material. `maxLengthSec` documents the platform's hard cap so a
 *      future "Use full length" toggle has the right ceiling.
 *   3. **Share metadata** — `filenameSlug` and `shareText` flow into the
 *      saved file name and the native share sheet so users don't have to
 *      retype context when they post.
 *
 * Adding a platform: add an entry to `SOCIAL_PRESETS`. The existing
 * `data-quickstart="<id>"` buttons in HTML pick it up by id automatically.
 */

import type { ConceptMode } from "../viz/scenes/bioclast";

/** Aspect tokens the existing `data-aspect` CSS framing supports. */
export type SocialAspect = "portrait" | "square" | "landscape";

export interface SocialPreset {
  /** Stable id used by `data-quickstart="<id>"` markup + Quick-Start UI. */
  id: string;
  /** User-facing label (currently rendered by the existing HTML). */
  label: string;
  /** Aspect token — drives `frame.dataset.aspect`. */
  aspect: SocialAspect;
  /** Exact encoded video width in pixels. */
  outputWidth: number;
  /** Exact encoded video height in pixels. */
  outputHeight: number;
  /** Default sweet-spot recording length, in seconds. */
  lengthSec: number;
  /** Platform's hard maximum clip length (informational; not enforced). */
  maxLengthSec: number;
  /**
   * Auto-cycle concepts during the clip. Keeps long clips visually
   * evolving instead of sitting on one preset.
   */
  autoCycle: boolean;
  /** Toggle the renderer's autopilot framing during the clip. */
  autopilot: boolean;
  /** Seconds between autopilot framing changes. */
  autopilotInterval: number;
  /**
   * Slug used for filenames: `pulsefx-<slug>-<timestamp>.<ext>`.
   * Lowercase, no spaces, no leading dash.
   */
  filenameSlug: string;
  /** Default text injected into native / Web Share sheets. */
  shareText: string;
  /**
   * Optional override list of concept modes to cycle through. When
   * unset, the UI falls back to its full cycleable concept catalog.
   * Useful for platforms that prefer a particular mood (e.g. Spotify
   * Canvas: no text-overlay-likely modes).
   */
  cycleConcepts?: ConceptMode[];
}

const PORTRAIT_OUTPUT = { outputWidth: 1080, outputHeight: 1920 } as const;
const SQUARE_OUTPUT = { outputWidth: 1080, outputHeight: 1080 } as const;
const LANDSCAPE_OUTPUT = { outputWidth: 1920, outputHeight: 1080 } as const;

export function getSocialOutputSize(
  aspect: SocialAspect | "fit" | string,
): { width: number; height: number } | null {
  if (aspect === "portrait") {
    return { width: PORTRAIT_OUTPUT.outputWidth, height: PORTRAIT_OUTPUT.outputHeight };
  }
  if (aspect === "square") {
    return { width: SQUARE_OUTPUT.outputWidth, height: SQUARE_OUTPUT.outputHeight };
  }
  if (aspect === "landscape") {
    return { width: LANDSCAPE_OUTPUT.outputWidth, height: LANDSCAPE_OUTPUT.outputHeight };
  }
  return null;
}

/**
 * Canonical platform list. Order matters — first entries are the most
 * common discovery-feed targets and tend to render first in chip rows.
 */
export const SOCIAL_PRESETS: SocialPreset[] = [
  {
    id: "tiktok",
    label: "TikTok",
    aspect: "portrait",
    ...PORTRAIT_OUTPUT,
    lengthSec: 30,
    // TikTok currently allows up to 10-minute uploads, but the discovery
    // feed favors clips under ~60s. We keep the default short.
    maxLengthSec: 600,
    autoCycle: true,
    autopilot: true,
    autopilotInterval: 6,
    filenameSlug: "tiktok",
    shareText: "Made with PulseFX 🎶",
  },
  {
    id: "reels",
    label: "Instagram Reels",
    aspect: "portrait",
    ...PORTRAIT_OUTPUT,
    lengthSec: 30,
    // Reels caps at 90s for in-app records, 15min for uploads.
    maxLengthSec: 90,
    autoCycle: true,
    autopilot: true,
    autopilotInterval: 6,
    filenameSlug: "reels",
    shareText: "🎶 Made with PulseFX — #reels",
  },
  {
    id: "story",
    label: "Instagram Story",
    aspect: "portrait",
    ...PORTRAIT_OUTPUT,
    // Each Story segment is 15s; longer clips get auto-split. Default to
    // a single segment so the share lands cleanly.
    lengthSec: 15,
    maxLengthSec: 15,
    autoCycle: false,
    autopilot: true,
    autopilotInterval: 5,
    filenameSlug: "ig-story",
    shareText: "🎶 PulseFX",
  },
  {
    id: "shorts",
    label: "YouTube Shorts",
    aspect: "portrait",
    ...PORTRAIT_OUTPUT,
    lengthSec: 60,
    // Shorts maxes at 60s.
    maxLengthSec: 60,
    autoCycle: true,
    autopilot: true,
    autopilotInterval: 8,
    filenameSlug: "shorts",
    shareText: "🎶 Made with PulseFX — #shorts",
  },
  {
    id: "square",
    label: "Square Post",
    aspect: "square",
    ...SQUARE_OUTPUT,
    lengthSec: 30,
    // Instagram in-feed video caps at 60s for Posts.
    maxLengthSec: 60,
    autoCycle: true,
    autopilot: true,
    autopilotInterval: 8,
    filenameSlug: "square",
    shareText: "🎶 Made with PulseFX",
  },
  {
    id: "x",
    label: "X / Twitter",
    aspect: "landscape",
    ...LANDSCAPE_OUTPUT,
    // X allows up to 140s on standard accounts.
    lengthSec: 30,
    maxLengthSec: 140,
    autoCycle: true,
    autopilot: true,
    autopilotInterval: 8,
    filenameSlug: "x",
    shareText: "🎶 Made with PulseFX",
  },
  {
    id: "youtube",
    label: "YouTube",
    aspect: "landscape",
    ...LANDSCAPE_OUTPUT,
    lengthSec: 60,
    // No practical cap for our purposes (12h hard limit).
    maxLengthSec: 60 * 60,
    autoCycle: true,
    autopilot: true,
    autopilotInterval: 10,
    filenameSlug: "youtube",
    shareText: "🎶 Made with PulseFX",
  },
  {
    id: "canvas",
    label: "Spotify Canvas",
    aspect: "portrait",
    ...PORTRAIT_OUTPUT,
    // Spotify Canvas spec: 3–8s loop, 1080x1920, no text overlays.
    lengthSec: 8,
    maxLengthSec: 8,
    // Loop cleanly — no concept changes mid-clip.
    autoCycle: false,
    autopilot: false,
    autopilotInterval: 8,
    filenameSlug: "spotify-canvas",
    shareText: "🎶 PulseFX Canvas",
  },
];

/** O(1) lookup by id; returns null when the id is unknown. */
const PRESET_BY_ID: ReadonlyMap<string, SocialPreset> = new Map(
  SOCIAL_PRESETS.map((p) => [p.id, p]),
);

export function getSocialPreset(id: string | null | undefined): SocialPreset | null {
  if (!id) return null;
  return PRESET_BY_ID.get(id) ?? null;
}

/**
 * Suggest a platform-aware filename:
 *   `pulsefx-<slug>-YYYY-MM-DDTHH-mm-ss.<ext>`
 *
 * Falls back to `pulsefx-YYYY-MM-DDTHH-mm-ss.<ext>` (matching the legacy
 * `suggestFilename` from `record/capture.ts`) when no preset is supplied,
 * so existing call sites that don't yet know about platforms are unchanged.
 */
export function suggestSocialFilename(ext: string, preset: SocialPreset | null): string {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, "0");
  const stamp =
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
  if (preset) return `pulsefx-${preset.filenameSlug}-${stamp}.${ext}`;
  return `pulsefx-${stamp}.${ext}`;
}
